import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import PageLayout from '../../layouts/PageLayout';
import { useBranch } from '../../context/BranchContext';
import { configurationAPI, qcSessionAPI } from '../../services';
import { toast } from 'react-toastify';

/**
 * Cấu hình phân phối phiên QC — sửa Configuration key `qc_config`.
 *
 * Trang cũ gọi `/qc/weekly-quota` (endpoint đã bị xoá khỏi server từ lâu) nên luôn lỗi.
 * Sau bản refactor, phiên QC không còn hạn mức theo từng loại bằng chứng
 * (IMAGE/CAMERA/FORM_DOCS) mà chỉ còn 3 tham số: tổng số mục / phiên, cửa sổ lấy việc,
 * và số mục tối thiểu bảo đảm cho từng nguồn.
 */

const CONFIG_KEY = 'qc_config';

const SOURCE_LABELS = {
  shift_task: 'Task ca làm',
  table_prep: 'Chuẩn bị bàn',
  game_check: 'Kiểm tra game',
  loyalty: 'Duyệt loyalty',
};

const SOURCE_KEYS = Object.keys(SOURCE_LABELS);

const DEFAULT_FORM = {
  items_per_session: 20,
  lookback_days: 30,
  type_min: { shift_task: 4, table_prep: 3, game_check: 1, loyalty: 2 },
  // #203002 — công tắc QC game, MẶC ĐỊNH TẮT. Bật lại được ngay tại đây.
  game_check_enabled: false,
};

const parseConfigValue = (raw) => {
  if (!raw) return null;
  try {
    return typeof raw === 'string' ? JSON.parse(raw) : raw;
  } catch {
    return null;
  }
};

const toForm = (parsed) => {
  if (!parsed) return { ...DEFAULT_FORM, type_min: { ...DEFAULT_FORM.type_min } };
  const typeMin = { ...DEFAULT_FORM.type_min };
  SOURCE_KEYS.forEach((key) => {
    const value = Number(parsed?.type_min?.[key]);
    if (Number.isFinite(value) && value >= 0) typeMin[key] = Math.floor(value);
  });
  return {
    items_per_session:
      Number(parsed.items_per_session) > 0
        ? Math.floor(Number(parsed.items_per_session))
        : DEFAULT_FORM.items_per_session,
    lookback_days:
      Number(parsed.lookback_days) > 0
        ? Math.floor(Number(parsed.lookback_days))
        : DEFAULT_FORM.lookback_days,
    type_min: typeMin,
    game_check_enabled:
      parsed?.game_check_enabled === true || parsed?.game_check_enabled === 'true',
  };
};

const QcQuotaSetupPage = () => {
  const { branches, selectedBranch } = useBranch();
  const [configs, setConfigs] = useState([]);
  const [scopeBranchId, setScopeBranchId] = useState('');
  const [form, setForm] = useState(() => toForm(null));
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [backlog, setBacklog] = useState(null);

  /** Config đang áp cho phạm vi đang chọn: riêng chi nhánh nếu có, không thì global. */
  const activeConfig = useMemo(() => {
    if (scopeBranchId) {
      return configs.find((c) => c.branch_id === scopeBranchId) || null;
    }
    return configs.find((c) => !c.branch_id) || null;
  }, [configs, scopeBranchId]);

  const globalConfig = useMemo(() => configs.find((c) => !c.branch_id) || null, [configs]);

  const fetchConfigs = useCallback(async () => {
    try {
      setLoading(true);
      const response = await configurationAPI.getConfigurations({ search: CONFIG_KEY });
      const list = Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : [];
      setConfigs(list.filter((c) => c.key === CONFIG_KEY));
    } catch (error) {
      console.error('Cannot fetch qc_config', error);
      toast.error(error?.message || 'Không tải được cấu hình QC');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchBacklog = useCallback(async () => {
    try {
      setBacklog(await qcSessionAPI.getQcBacklog());
    } catch (error) {
      console.error('Cannot fetch QC backlog', error);
      setBacklog(null);
    }
  }, []);

  useEffect(() => {
    fetchConfigs();
    fetchBacklog();
  }, [fetchConfigs, fetchBacklog]);

  useEffect(() => {
    if (selectedBranch?.id) setScopeBranchId('');
  }, [selectedBranch]);

  // Đổi phạm vi → nạp lại giá trị đang áp dụng cho phạm vi đó
  useEffect(() => {
    setForm(toForm(parseConfigValue(activeConfig?.value)));
  }, [activeConfig]);

  const totalTypeMin = useMemo(
    () => SOURCE_KEYS.reduce((sum, key) => sum + (Number(form.type_min[key]) || 0), 0),
    [form]
  );

  const setTypeMin = (key, value) => {
    setForm((prev) => ({
      ...prev,
      type_min: { ...prev.type_min, [key]: Math.max(0, Number(value) || 0) },
    }));
  };

  const handleSave = async () => {
    if (form.items_per_session < 1) {
      toast.error('Số mục mỗi phiên phải từ 1 trở lên');
      return;
    }
    if (form.lookback_days < 1) {
      toast.error('Cửa sổ lấy việc phải từ 1 ngày trở lên');
      return;
    }

    // #203002 — giữ nguyên MỌI field cũ trong qc_config (vd min_items_for_evaluation)
    // rồi mới ghi đè các field trang này quản, tránh nuốt mất cấu hình người khác.
    const existingValue = parseConfigValue(activeConfig?.value) || {};
    const payloadValue = JSON.stringify(
      {
        ...existingValue,
        items_per_session: form.items_per_session,
        lookback_days: form.lookback_days,
        type_min: form.type_min,
        game_check_enabled: form.game_check_enabled,
      },
      null,
      2
    );

    try {
      setSaving(true);
      if (activeConfig) {
        await configurationAPI.updateConfiguration(activeConfig.id, { value: payloadValue });
      } else {
        await configurationAPI.createConfiguration({
          key: CONFIG_KEY,
          value: payloadValue,
          type: 'JSON',
          category: 'PERFORMANCE',
          description:
            'Cấu hình phân phối phiên QC (số mục/phiên, cửa sổ lấy việc, tối thiểu mỗi nguồn, bật/tắt QC game)',
          branch_id: scopeBranchId || undefined,
          is_active: true,
        });
      }
      toast.success('Đã lưu cấu hình QC');
      await Promise.all([fetchConfigs(), fetchBacklog()]);
    } catch (error) {
      console.error('Cannot save qc_config', error);
      toast.error(error?.message || 'Không lưu được cấu hình QC');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteOverride = async () => {
    if (!activeConfig || !scopeBranchId) return;
    if (!window.confirm('Xoá cấu hình riêng của chi nhánh này? Chi nhánh sẽ dùng lại cấu hình chung.')) {
      return;
    }
    try {
      setSaving(true);
      await configurationAPI.deleteConfiguration(activeConfig.id);
      toast.success('Đã xoá cấu hình riêng, chi nhánh dùng lại cấu hình chung');
      await fetchConfigs();
    } catch (error) {
      console.error('Cannot delete qc_config override', error);
      toast.error(error?.message || 'Không xoá được cấu hình riêng');
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageLayout>
      <div className="mc-page">
        <div className="mc-page-header my-2">
          <div className="mc-page-header-title">
            <h1>Cấu hình QC</h1>
            <small className="text-muted">
              Quyết định mỗi phiên QC to bao nhiêu và lấy việc trong bao lâu đổ lại.
            </small>
          </div>
          <div className="mc-page-header-actions d-flex gap-2">
            <Button
              variant="outline-secondary"
              onClick={fetchConfigs}
              disabled={loading}
            >
              {loading ? 'Đang tải...' : 'Làm mới'}
            </Button>
          </div>
        </div>

        <Alert variant="info">
          Nhân viên nhận nhiệm vụ QC tới đâu, hệ thống sinh phiên tới đó. Mỗi phiên lấy{' '}
          <strong>{form.items_per_session} mục</strong> gộp cả{' '}
          <strong>{form.game_check_enabled ? 4 : 3} nguồn</strong>, ưu tiên{' '}
          <strong>việc mới hoàn thành trước</strong> rồi lùi dần về cũ, chỉ trong phạm vi{' '}
          <strong>{form.lookback_days} ngày</strong> gần nhất. Việc cũ hơn khoảng đó sẽ không được
          soi nữa.
        </Alert>

        <Row>
          <Col lg={7}>
            <Card className="mb-4 shadow-sm">
              <Card.Header>
                <h5 className="mb-0">Tham số phân phối</h5>
              </Card.Header>
              <Card.Body>
                <Row className="g-3">
                  <Col md={12}>
                    <Form.Label>Áp dụng cho</Form.Label>
                    <Form.Select
                      value={scopeBranchId}
                      onChange={(e) => setScopeBranchId(e.target.value)}
                    >
                      <option value="">Tất cả chi nhánh (cấu hình chung)</option>
                      {branches.map((branch) => (
                        <option
                          key={branch.id}
                          value={branch.id}
                        >
                          Riêng {branch.name}
                        </option>
                      ))}
                    </Form.Select>
                    {scopeBranchId && !activeConfig && (
                      <Form.Text muted>
                        Chi nhánh này chưa có cấu hình riêng — đang chạy theo cấu hình chung. Bấm lưu
                        để tạo cấu hình riêng.
                      </Form.Text>
                    )}
                  </Col>

                  <Col md={6}>
                    <Form.Label>Số mục mỗi phiên *</Form.Label>
                    <Form.Control
                      type="number"
                      min="1"
                      value={form.items_per_session}
                      onChange={(e) =>
                        setForm({ ...form, items_per_session: Math.max(1, Number(e.target.value) || 0) })
                      }
                    />
                    <Form.Text muted>Gộp cả 4 nguồn. Trước đây có phiên tới 57 mục.</Form.Text>
                  </Col>

                  <Col md={6}>
                    <Form.Label>Cửa sổ lấy việc (ngày) *</Form.Label>
                    <Form.Control
                      type="number"
                      min="1"
                      value={form.lookback_days}
                      onChange={(e) =>
                        setForm({ ...form, lookback_days: Math.max(1, Number(e.target.value) || 0) })
                      }
                    />
                    <Form.Text muted>Chỉ lấy việc hoàn thành trong ngần này ngày đổ lại.</Form.Text>
                  </Col>

                  <Col md={12}>
                    <Form.Check
                      type="switch"
                      id="qc-game-check-enabled"
                      label="Chấm QC phiên kiểm game"
                      checked={form.game_check_enabled}
                      onChange={(e) =>
                        setForm({ ...form, game_check_enabled: e.target.checked })
                      }
                    />
                    <Form.Text muted>
                      {form.game_check_enabled
                        ? 'Đang BẬT: phiên QC có bốc mục kiểm game như trước.'
                        : 'Đang TẮT (mặc định): phiên QC không bốc mục kiểm game; mục kiểm game chưa chấm bị nhả khỏi phiên. Mục đã chấm giữ nguyên. Bật lại để chạy như cũ.'}
                    </Form.Text>
                  </Col>

                  <Col md={12}>
                    <Form.Label className="mb-1">Tối thiểu mỗi nguồn</Form.Label>
                    <p className="text-muted small mb-2">
                      Bảo đảm nguồn ít việc vẫn được soi. Không có ràng buộc này thì task ca — nguồn
                      đông nhất — chiếm sạch số mục của phiên.
                    </p>
                    <Row className="g-2">
                      {SOURCE_KEYS.map((key) => (
                        <Col
                          md={3}
                          key={key}
                        >
                          <Form.Label className="small">{SOURCE_LABELS[key]}</Form.Label>
                          <Form.Control
                            type="number"
                            min="0"
                            value={form.type_min[key]}
                            onChange={(e) => setTypeMin(key, e.target.value)}
                            disabled={key === 'game_check' && !form.game_check_enabled}
                          />
                        </Col>
                      ))}
                    </Row>
                    {totalTypeMin > form.items_per_session && (
                      <Alert
                        variant="warning"
                        className="mt-2 mb-0 py-2 small"
                      >
                        Tổng tối thiểu ({totalTypeMin}) lớn hơn số mục mỗi phiên (
                        {form.items_per_session}). Hệ thống sẽ chia đều vòng tròn cho các nguồn, không
                        nguồn nào được đủ mức tối thiểu.
                      </Alert>
                    )}
                  </Col>
                </Row>
              </Card.Body>
              <Card.Footer className="d-flex gap-2 justify-content-end">
                {scopeBranchId && activeConfig && (
                  <Button
                    variant="outline-danger"
                    onClick={handleDeleteOverride}
                    disabled={saving}
                  >
                    Xoá cấu hình riêng
                  </Button>
                )}
                <Button
                  variant="primary"
                  onClick={handleSave}
                  disabled={saving || loading}
                >
                  {saving ? <Spinner size="sm" animation="border" /> : 'Lưu cấu hình'}
                </Button>
              </Card.Footer>
            </Card>
          </Col>

          <Col lg={5}>
            <Card className="mb-4 shadow-sm">
              <Card.Header>
                <h5 className="mb-0">Việc đang chờ QC</h5>
                <small className="text-muted">
                  Đối chiếu với số mục mỗi phiên để biết cần bao nhiêu lượt soi mới hết tồn.
                </small>
              </Card.Header>
              <Card.Body className="p-0">
                {!backlog?.branches?.length ? (
                  <p className="text-muted text-center py-4 mb-0">Chưa có dữ liệu.</p>
                ) : (
                  <div className="table-responsive">
                    <Table
                      size="sm"
                      className="mb-0"
                    >
                      <thead>
                        <tr>
                          <th>Chi nhánh</th>
                          <th className="text-end">Đang chờ</th>
                          <th className="text-end">Phiên đã xong</th>
                        </tr>
                      </thead>
                      <tbody>
                        {backlog.branches.map((b) => (
                          <tr key={b.branch_id}>
                            <td>{b.branch_name}</td>
                            <td className="text-end fw-semibold">{b.pending_items}</td>
                            <td className="text-end">{b.completed_sessions}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Card.Body>
            </Card>

            {globalConfig && (
              <Card className="shadow-sm">
                <Card.Header>
                  <h6 className="mb-0">Giá trị đang lưu (cấu hình chung)</h6>
                </Card.Header>
                <Card.Body>
                  <pre className="small mb-0">{globalConfig.value}</pre>
                </Card.Body>
              </Card>
            )}
          </Col>
        </Row>
      </div>
    </PageLayout>
  );
};

export default QcQuotaSetupPage;
